import { useState, useCallback } from 'react';
import { Contract, keccak256, toUtf8Bytes } from 'ethers';
import html2canvas from 'html2canvas';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../constants';
import { uploadImageToPinata, uploadMetadataToPinata, generateMetadata } from '../utils';

export const MINT_STAGES = {
  IDLE: 'idle',
  CAPTURING: 'capturing',
  UPLOADING_IMAGE: 'uploading_image',
  UPLOADING_METADATA: 'uploading_metadata',
  AWAITING_APPROVAL: 'awaiting_approval',
  CONFIRMING: 'confirming',
  SUCCESS: 'success',
  ERROR: 'error',
};

export const useMint = (provider) => {
  const [stage, setStage] = useState(MINT_STAGES.IDLE);
  const [error, setError] = useState(null);
  const [txHash, setTxHash] = useState(null);
  const [tokenId, setTokenId] = useState(null);

  const mint = useCallback(async (ticketRef, flightData) => {
    if (!provider) {
      setError('Wallet not connected');
      setStage(MINT_STAGES.ERROR);
      return null;
    }

    setError(null);
    setTxHash(null);
    setTokenId(null);

    try {
      // Stage 1: Capture ticket image
      setStage(MINT_STAGES.CAPTURING);
      const canvas = await html2canvas(ticketRef, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
        windowHeight: ticketRef.scrollHeight,
        height: ticketRef.scrollHeight,
      });
      
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/png', 1.0)
      );

      // Stage 2: Upload image to IPFS
      setStage(MINT_STAGES.UPLOADING_IMAGE);
      const imageURI = await uploadImageToPinata(blob, `flight-${flightData.flightNumber}-${Date.now()}.png`);

      // Stage 3: Upload metadata to IPFS
      setStage(MINT_STAGES.UPLOADING_METADATA);
      const metadata = generateMetadata(flightData, imageURI);
      const metadataURI = await uploadMetadataToPinata(metadata, `metadata-${Date.now()}.json`);

      // Stage 4: Await user approval
      setStage(MINT_STAGES.AWAITING_APPROVAL);
      const signer = await provider.getSigner();
      const contract = new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
      const mintPrice = await contract.mintPrice();
      
      // Generate data hash for verification
      const dataHash = keccak256(toUtf8Bytes(JSON.stringify(flightData)));
      const signerAddress = await signer.getAddress();

      // Send transaction
      const tx = await contract.mintFlightNFT(
        signerAddress,
        metadataURI,
        dataHash,
        { value: mintPrice }
      );

      setTxHash(tx.hash);

      // Stage 5: Wait for confirmation
      setStage(MINT_STAGES.CONFIRMING);
      const receipt = await tx.wait();

      // Try to get token ID from logs
      // The Transfer event should have the token ID
      const transferLog = receipt.logs.find(log => log.topics.length === 4);
      if (transferLog) {
        const tokenIdBigInt = BigInt(transferLog.topics[3]);
        setTokenId(tokenIdBigInt.toString());
      }

      // Stage 6: Success!
      setStage(MINT_STAGES.SUCCESS);
      
      return {
        txHash: receipt.hash,
        tokenId: tokenId,
        imageURI,
        metadataURI,
      };
    } catch (err) {
      console.error('Minting failed:', err);
      setError(err.message || 'Minting failed');
      setStage(MINT_STAGES.ERROR);
      return null;
    }
  }, [provider, tokenId]);

  const reset = useCallback(() => {
    setStage(MINT_STAGES.IDLE);
    setError(null);
    setTxHash(null);
    setTokenId(null);
  }, []);

  return {
    stage,
    error,
    txHash,
    tokenId,
    mint,
    reset,
    isMinting: stage !== MINT_STAGES.IDLE && stage !== MINT_STAGES.SUCCESS && stage !== MINT_STAGES.ERROR,
  };
};
